import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import {
  CreateWorksheetsSchema,
  UpdateWorksheetsSchema,
} from "./worksheets.schema";

export const WorksheetsIdInputSchema = z.object({
  id: z.string().trim().min(1),
  businessId: z.string().trim().min(1),
});

export const ListWorksheetsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  search: z.string().optional(),
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  ids: z.array(z.string().trim().min(1)).max(500).optional(),
});

export const CreateWorksheetsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  item: CreateWorksheetsSchema,
});

export const UpdateWorksheetsInputSchema = z.object({
  ...WorksheetsIdInputSchema.shape,
  updates: UpdateWorksheetsSchema,
});

export const listWorksheets = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => ListWorksheetsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("worksheets.list", async () => {
      const { listWorksheetsForUser } = await import("./worksheets.server");
      return listWorksheetsForUser(context.user.id, data.businessId, {
        search: data.search,
        offset: data.offset,
        limit: data.limit,
        ids: data.ids,
      });
    });
  });

export const getWorksheets = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => WorksheetsIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("worksheets.get", async () => {
      const { getWorksheetsForUser } = await import("./worksheets.server");
      const result = await getWorksheetsForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const createWorksheets = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => CreateWorksheetsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("worksheets.create", async () => {
      const { createWorksheetsForUser } = await import("./worksheets.server");
      return createWorksheetsForUser(
        context.user.id,
        data.businessId,
        data.item,
      );
    });
  });

export const updateWorksheets = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => UpdateWorksheetsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("worksheets.update", async () => {
      const { updateWorksheetsForUser } = await import("./worksheets.server");
      const result = await updateWorksheetsForUser(
        context.user.id,
        data.id,
        data.updates,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const deleteWorksheets = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => WorksheetsIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("worksheets.delete", async () => {
      const { deleteWorksheetsForUser } = await import("./worksheets.server");
      const result = await deleteWorksheetsForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });
